import { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";
import api from "../lib/api";

export default function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { id: "welcome", role: "assistant", text: "Hello — I'm AIRA, the TileRoom concierge. Ask me anything about tiles, sizes, room calculations, or how to shop the collection." },
  ]);
  const [input, setInput] = useState("");
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const now = Date.now();
    setMsgs(m => [...m, { id: `u-${now}`, role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: text, session_id: session });
      if (data.session_id) setSession(data.session_id);
      setMsgs(m => [...m, { id: `a-${Date.now()}`, role: "assistant", text: data.reply, offline: !!data.offline }]);
    } catch (e) {
      setMsgs(m => [...m, { id: `a-${Date.now()}`, role: "assistant", text: "Please try again in a moment.", offline: true }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-50 w-16 h-16 bg-[#1E2C5A] text-white flex items-center justify-center shadow-[0_8px_28px_rgba(30,44,90,0.35)] hover:scale-105 transition-transform border-2 border-[#C9A968]"
          data-testid="chat-assistant-open"
          aria-label="Open AIRA — TileRoom concierge chat"
        >
          <Sparkles className="w-6 h-6" strokeWidth={1.5} />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[92vw] sm:w-[380px] max-w-[420px] h-[560px] bg-[#FAF9F6] shadow-[0_20px_60px_rgba(0,0,0,0.35)] flex flex-col border border-[#1A1A1A]/15"
          data-testid="chat-assistant-panel"
        >
          <div className="bg-[#1E2C5A] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#C9A968]" strokeWidth={1.5} />
              <div>
                <p className="text-sm tracking-wider">AIRA · TileRoom Concierge</p>
                <p className="text-[10px] text-white/60 uppercase tracking-[0.2em]">Powered by AI</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="chat-assistant-close">
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
            {msgs.map((m, i) => (
              <div
                key={m.id || `${m.role}-${i}`}
                className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[#1E2C5A] text-white"
                    : "mr-auto bg-white border border-[#1A1A1A]/10 text-[#1A1A1A]"
                }`}
                data-testid={`chat-msg-${m.role}-${i}`}
              >
                {m.text}
              </div>
            ))}
            {busy && (
              <div className="mr-auto bg-white border border-[#1A1A1A]/10 text-[#8A8A8A] italic text-sm px-4 py-3">Thinking…</div>
            )}
          </div>

          <div className="border-t border-[#1A1A1A]/10 p-3 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              disabled={busy}
              placeholder="Ask AIRA — tiles, sizes, delivery…"
              className="flex-1 bg-transparent border-b border-[#d6d3ce] py-2 outline-none focus:border-[#1E2C5A] text-sm disabled:opacity-50"
              data-testid="chat-input"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              className="w-10 h-10 flex items-center justify-center bg-[#1E2C5A] text-white disabled:opacity-30"
              data-testid="chat-send"
              aria-label="Send"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
